import React from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Phone,
  Star,
  Crown,
  User,
  Settings,
  RefreshCw,
  Eye,
  BadgeAlert,
  VerifiedIcon,
  Bell,
  X,
  Share2,
  Download,
  Pencil,
  Plus,
  Trash2,
  Mic,
  Share,
  Copy,
  ArrowLeft,
  MessageCircle,
  Heart,
} from "lucide-react";
import { toast } from "react-toastify";
import { getRequest, postRequest, putRequest } from "../../helpers/api.helper";
import { UPLOAD_URL } from "../../utils/constants";
import { getGenderPlaceholder } from "../../helpers/matches.helper";
import Gnavbar from "../../components/Gnavbar";
import BottomNavbar from "../../components/BottomNavbar";
import { ProfileType } from "../../types";
import ProfilePrivacyAndInsights from "../../components/ProfilePrivacyAndInsights";
import PaidContactDetails from "../../components/PaidContactDetails";
import ProfileSection from "./Components/ProfileSection";
import GroupedProfileSection from "./Components/GroupedProfileSection";
import ProfileImage from "./Components/ProfileImage";
import { isAccountSuspended } from "../../utils/bureau";
import ProfileGallery from "./Components/ProfileGallery";
import {
  formatCreatedBy,
  isCreatedByOtherMediator,
  isCreatedBySuperAdmin,
} from "../../utils/createdBy";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { hasActiveMembership } from "../../utils/bureau";
import { useLanguage } from "../../context/LanguageContext";
import DownloadModal from "../ManageProfile/modals/DownloadModal";
import ShareModal from "../../components/ShareModal";
import ShareBiodataModal from "../../components/ShareBiodataModal";
import ServicePreferenceModal from "../ManageProfile/modals/ServicePreferenceModal";
import { VoicePlayer } from "../../components/VoicePlayer";
import VoiceMessageField from "../../components/VoiceMessageField";
import {
  resolveVoiceMessageUrl,
  uploadVoiceMessage,
} from "../../helpers/voiceMessage.helper";
import { shareVoiceMessageOnWhatsApp } from "../../helpers/profileExport.helper";
import { getEmploymentLabel } from "../../utils/employmentOptions";

type PartnerPreferencesUI = {
  matchScore?: string;
  age: string;
  height: string;
  maritalStatus: string;
  motherTongue: string;
  physicalStatus: string;
  eatingHabits: string;
  smokingHabits: string;
  drinkingHabits: string;
  religion: string;
  caste: string;
  subcaste: string;
  star: string;
  dosham: string;
  education: string;
  employmentType: string;
  occupation: string;
  income: string;
  country: string;
  state: string;
  city: string;
};

type SuggestionUI = {
  id: string;
  name: string;
  details: string;
  location?: string;
  image: string;
};

const formatLabel = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  return value
    .toString()
    .replace(/[_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .filter(Boolean)
    .map((token) => token[0].toUpperCase() + token.slice(1).toLowerCase())
    .join(" ");
};

const formatHeight = (value?: string | number | null): string => {
  if (value === undefined || value === null) return "Not specified";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    const text = value.toString().trim();
    return text || "Not specified";
  }
  return `${numeric} ft`;
};

const joinList = (value?: string | string[] | null): string | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) {
    const entries = value
      .map((item) => formatLabel(item) ?? item)
      .filter(Boolean);
    return entries.length ? entries.join(", ") : undefined;
  }
  return formatLabel(value) ?? value;
};

const copyTextToClipboard = async (
  value: string | undefined,
  successMessage: string,
  errorMessage: string,
) => {
  if (!value) {
    toast.error(errorMessage);
    return;
  }

  try {
    await navigator.clipboard.writeText(value);
    toast.success(successMessage);
  } catch (error) {
    console.error("Failed to copy text", error);
    toast.error(errorMessage);
  }
};

const buildAboutText = (profile: Record<string, any>) => {
  const summary = Array.isArray(profile.customWords) ? profile.customWords : [];
  const otherBusiness =
    profile.otherBusiness && profile.otherBusiness !== "null"
      ? String(profile.otherBusiness).trim()
      : "";

  const chunks: string[] = [];
  if (summary.length) chunks.push(summary.join(" "));
  // if (skills.length) chunks.push(`Talented in ${skills.join(", ")}.`);
  if (otherBusiness) chunks.push(`Other business: ${otherBusiness}.`);

  return chunks.length
    ? chunks.join(" ")
    : "No additional information provided.";
};
const buildTalents = (profile: Record<string, any>) => {
  const skills = Array.isArray(profile.extraTalentedSkills)
    ? profile.extraTalentedSkills
        .map((entry: unknown) => String(entry ?? "").trim())
        .filter(Boolean)
    : [];

  return skills.join(", ");
};

const formatEmploymentStatus = (value?: string | null) => {
  if (!value) return undefined;
  return getEmploymentLabel(value);
};

const ProfileDetails = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const hasPaidMembership = hasActiveMembership(bureauInfo?.paymentStatus);

  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [paymentUpdating, setPaymentUpdating] = useState<string | null>(null);
  const [showFullNumber, setShowFullNumber] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const { t } = useLanguage();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsPanelRef = React.useRef<HTMLDivElement>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareBiodataModalOpen, setIsShareBiodataModalOpen] = useState(false);
  const [shareBiodataType, setShareBiodataType] = useState<
    "pdf" | "image" | null
  >(null);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isVoiceEditorOpen, setIsVoiceEditorOpen] = useState(false);
  const [voiceFile, setVoiceFile] = useState<File | null>(null);
  const [removeExistingVoice, setRemoveExistingVoice] = useState(false);
  const [isVoiceSaving, setIsVoiceSaving] = useState(false);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isVoiceSharing, setIsVoiceSharing] = useState(false);
  const [isMoveToDirectModalOpen, setIsMoveToDirectModalOpen] = useState(false);
  const [moveToDirectLoading, setMoveToDirectLoading] = useState(false);

  useEffect(() => {
    setIsSettingsOpen(false);
  }, [profileId]);

  useEffect(() => {
    if (!isSettingsOpen) return;
    const panel = settingsPanelRef.current;
    if (!panel) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.focus();
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      panel.animate?.(
        [{ transform: "translateY(100%)" }, { transform: "translateY(0)" }],
        { duration: 250, easing: "ease-out" },
      );
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsSettingsOpen(false);
      }
      if (event.key === "Tab") {
        const focusable = Array.from(panel.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
        )).filter((element) => element.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel)) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    panel.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      panel.removeEventListener("keydown", handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isSettingsOpen]);

  const reportReasons = useMemo(
    () => [
      "Inappropriate Content",
      "Fake Profile",
      "Spam or Scam",
      "Harassment",
      "Impersonation",
      "Misleading Information",
      "Other",
    ],
    [],
  );
  const [isServicePreferenceModalOpen, setIsServicePreferenceModalOpen] =
    useState(false);
  const selectedProfileMartialId = useMemo(() => {
    if (!profile) return null;
    const candidate = profile as unknown as Record<string, any>;
    return candidate?.maritalId;
  }, [profile]);
  const advancedSearchContext = useMemo(() => {
    const state = (location.state as Record<string, any> | null) ?? null;
    if (state?.source !== "advanced-search-results") {
      return null;
    }

    const profileIds = Array.isArray(state.profileIds)
      ? state.profileIds.filter(Boolean)
      : [];

    return {
      returnTo:
        typeof state.returnTo === "string" && state.returnTo
          ? state.returnTo
          : "/admin/advanced-search-results",
      searchData: state.searchData ?? null,
      profileIds,
      currentIndex:
        typeof state.currentIndex === "number" ? state.currentIndex : -1,
      selectedProfileId:
        typeof state.selectedProfileId === "string"
          ? state.selectedProfileId
          : null,
    };
  }, [location.state]);

  const fetchProfile = async () => {
    if (!profileId) {
      setError("Profile not found.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await getRequest(
        `/bureau/profile/${encodeURIComponent(profileId)}`,
      );
      if (!response.success || !response.data) {
        setError(response.message ?? "Unable to load profile details.");
        return;
      }
      setProfile(response.data);
      setShowFullNumber(false);
      setVoiceFile(null);
      setRemoveExistingVoice(false);
      setIsVoiceEditorOpen(false);
    } catch (err: any) {
      console.error("Failed to fetch profile detail:", err);
      setError("Unable to load profile details right now.");
    } finally {
      setLoading(false);
    }
  };
  const hasIncrementedView = React.useRef(false);

  useEffect(() => {
    fetchProfile();
    if (!hasIncrementedView.current && profileId) {
      hasIncrementedView.current = true;
      postRequest(`/common/profile/${profileId}/view`, {}).catch(() => {});
    }
  }, [profileId]);

  useEffect(() => {
    const handleShareBiodata = (event: any) => {
      const { type } = event.detail;
      setShareBiodataType(type);
      setIsShareBiodataModalOpen(true);
    };
    window.addEventListener("shareBiodata", handleShareBiodata);
    return () => {
      window.removeEventListener("shareBiodata", handleShareBiodata);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 ">
        <div className="flex flex-col items-center space-y-4 text-purple-600">
          <svg
            className="animate-spin h-8 w-8"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <p className="text-sm font-semibold">Loading profile…</p>
        </div>
      </div>
    );
  }
  const handleManageProfile = async () => {
    navigate(`/admin/profile/manage/${profile?.martialId}`);
  };

  const handlePaymentStatusChange = async (
    nextStatus: "free" | "paid" | "redzone",
  ) => {
    if (!profile?._id) {
      toast.error("Unable to determine profile ID.");
      return;
    }
    setPaymentUpdating(nextStatus);
    const response = await putRequest(`/bureau/profile/${profile._id}`, {
      paymentStatus: nextStatus,
      followUpStatus: "none",
    });
    if (response?.success) {
      setProfile((prev) =>
        prev
          ? { ...prev, paymentStatus: nextStatus, followUpStatus: "none" }
          : prev,
      );
      toast.success("Payment status updated.");
    } else {
      toast.error(response?.message || "Unable to update payment status.");
    }
    setPaymentUpdating(null);
  };

  const handleFollowUpStatusChange = async (
    nextStatus: "callback" | "not_responding",
  ) => {
    if (!profile?._id) {
      toast.error("Unable to determine profile ID.");
      return;
    }

    setPaymentUpdating(nextStatus);
    const response = await putRequest(`/bureau/profile/${profile._id}`, {
      followUpStatus: nextStatus,
    });

    if (response?.success) {
      setProfile((prev) =>
        prev ? { ...prev, followUpStatus: nextStatus } : prev,
      );
      toast.success("Follow-up status updated.");
    } else {
      toast.error(response?.message || "Unable to update follow-up status.");
    }
    setPaymentUpdating(null);
  };

  const formatDate = (isoString: string): string => {
    if (!isoString) return "-";

    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    // Show relative time only if within 30 days
    if (diffDay < 30) {
      if (diffSec < 60) return "just now";
      if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
      if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
      return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
    }

    // Otherwise show formatted date
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const openReportModal = () => {
    setReportReason("");
    setReportDescription("");
    setIsReportModalOpen(true);
  };

  const closeReportModal = () => {
    setIsReportModalOpen(false);
    setReportReason("");
    setReportDescription("");
  };

  const handleReportSubmit = async () => {
    if (!profile?._id) {
      toast.error("Unable to determine profile ID for report");
      return;
    }

    if (!reportReason.trim()) {
      toast.error("Please select a reason for reporting");
      return;
    }

    setReportLoading(true);
    try {
      const payload: Record<string, unknown> = {
        reportedProfileId: profile._id,
        reportedProfileName: profile.fullName,
        reason: reportReason,
        description: reportDescription,
        reportedAt: new Date().toISOString(),
      };

      if (selectedProfileMartialId) {
        payload.reportedMartialId = selectedProfileMartialId;
      }

      const response = await postRequest(`/bureau/profiles/report`, payload);

      if (response?.success) {
        toast.success(response?.message ?? "Profile reported successfully");
        closeReportModal();
      } else {
        toast.error(response?.message ?? "Failed to submit report");
      }
    } catch (error) {
      console.error("Error reporting profile:", error);
      toast.error("Error reporting profile. Please try again.");
    } finally {
      setReportLoading(false);
    }
  };
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-md p-6 text-center space-y-4">
          <h2 className="text-lg font-semibold text-red-600">
            {error ?? "Profile not available."}
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center px-5 py-2 rounded-lg bg-purple-600 text-white font-semibold shadow hover:bg-purple-700 transition"
          >
            {t("Go Back")}
          </button>
        </div>
      </div>
    );
  }

  const isMediatorCreatedProfile = isCreatedByOtherMediator(profile.createdBy);
  const hideContactDetailsForProfile = isCreatedBySuperAdmin(profile.createdBy);
  // const handleShareProfile = async () => {
  //   try {
  //     const targetId = profile.martialId;

  //     if (!targetId) return toast.warn("Profile ID missing");
  //     const bureauSlug = (profile?.bureauName ?? "").replace(/\s+/g, "");
  //     const shareUrl = `https://matrimonystudio.in/${profile.bureauId}/${bureauSlug}/${targetId}`;
  //     const shareText = `💍 Check out this profile on MeeMarriage: `;

  //     const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  //     // ✅ Capacitor native share
  //     if (Capacitor.isNativePlatform()) {
  //       await Share.share({
  //         title: "MeeMarriage Profile",
  //         text: "💍 Check out this profile on MeeMarriage",
  //         url: shareUrl,
  //         dialogTitle: "Share Profile",
  //       });
  //       return;
  //     }
  //     // ✅ Mobile → Native Share
  //     if (isMobile && navigator.share) {
  //       await navigator.share({
  //         title: "MeeMarriage Profile",
  //         text: shareText,
  //         url: shareUrl,
  //       });
  //       return;
  //     }

  //     // ✅ Desktop fallback → Copy
  //     await navigator.clipboard.writeText(shareUrl);
  //     toast.info("Profile link copied. You can share it anywhere.");
  //   } catch (error) {
  //     console.error("Share failed:", error);
  //     // toast.error("Couldn't share profile");
  //   }
  // };

  // Service preference functions
  const handleMoveToPaid = () => {
    if (!hasPaidMembership) {
      navigate("/admin/membership/plans", {
        state: { fromShare: true },
      });
      return;
    }

    setIsServicePreferenceModalOpen(true);
  };

  const closeServicePreferenceModal = () => {
    setIsServicePreferenceModalOpen(false);
  };

  const handleShareProfile = () => {
    setIsShareModalOpen(true);
  };

  const handleMoveToDirectProfiles = async () => {
    if (!profile?._id) {
      toast.error("Unable to determine profile ID.");
      return;
    }

    try {
      setMoveToDirectLoading(true);
      const response = await postRequest("/bureau/profiles/move-to-direct", {
        userId: profile._id,
      });

      if (!response?.success) {
        toast.error(response?.message || "Unable to move profile.");
        return;
      }

      toast.success(
        response?.message || "Profile moved to direct registered profiles.",
      );
      setIsMoveToDirectModalOpen(false);
      navigate(-1);
    } catch (error) {
      console.error("Error moving profile to direct profiles:", error);
      toast.error("Unable to move profile.");
    } finally {
      setMoveToDirectLoading(false);
    }
  };

  const handleSaveVoiceMessage = async () => {
    if (!profile?._id) {
      toast.error("Unable to determine profile ID.");
      return;
    }

    if (isVoiceRecording) {
      toast.info("Please stop voice recording before saving.");
      return;
    }

    if (!voiceFile && !removeExistingVoice) {
      setIsVoiceEditorOpen(false);
      return;
    }

    try {
      setIsVoiceSaving(true);
      let voiceMessage = removeExistingVoice ? "" : profile.voiceMessage || "";

      if (voiceFile) {
        const uploaded = await uploadVoiceMessage(voiceFile, profile._id);
        voiceMessage = uploaded.fileUrl;
      }

      const response = await putRequest(`/bureau/profile/${profile._id}`, {
        voiceMessage,
      });

      if (!response?.success) {
        toast.error(response?.message || "Unable to update voice message.");
        return;
      }

      setProfile((prev) => (prev ? { ...prev, voiceMessage } : prev));
      setVoiceFile(null);
      setRemoveExistingVoice(false);
      setIsVoiceEditorOpen(false);
      toast.success(
        voiceMessage
          ? "Voice message updated successfully."
          : "Voice message removed successfully.",
      );
    } catch (error: any) {
      console.error("Error updating voice message:", error);
      toast.error(error?.message || "Unable to update voice message.");
    } finally {
      setIsVoiceSaving(false);
    }
  };

  const handleRemoveVoiceMessage = async () => {
    if (!profile?._id || !profile.voiceMessage) {
      return;
    }

    try {
      setIsVoiceSaving(true);
      const response = await putRequest(`/bureau/profile/${profile._id}`, {
        voiceMessage: "",
      });

      if (!response?.success) {
        toast.error(response?.message || "Unable to remove voice message.");
        return;
      }

      setProfile((prev) => (prev ? { ...prev, voiceMessage: "" } : prev));
      setVoiceFile(null);
      setRemoveExistingVoice(false);
      setIsVoiceEditorOpen(false);
      toast.success("Voice message removed successfully.");
    } catch (error: any) {
      console.error("Error removing voice message:", error);
      toast.error(error?.message || "Unable to remove voice message.");
    } finally {
      setIsVoiceSaving(false);
    }
  };

  const handleShareVoiceMessageOnWhatsApp = async () => {
    if (!profile?.voiceMessage) {
      toast.error("Voice message is not available to share.");
      return;
    }

    try {
      setIsVoiceSharing(true);
      await shareVoiceMessageOnWhatsApp(profile.voiceMessage, {
        profileName: profile?.fullName,
        profileId: profile?.martialId,
      });
    } catch (error: any) {
      console.error("Error sharing voice message:", error);
      toast.error(error?.message || "Unable to share voice message.");
    } finally {
      setIsVoiceSharing(false);
    }
  };

const handleBackToResults = () => {
    const openedFromNotification = Boolean(
      (
        location.state as { openedFromNotification?: boolean } | null
      )?.openedFromNotification,
    );

    if (openedFromNotification) {
      navigate("/admin/dashboard", { replace: true });
      return;
    }

    if (!advancedSearchContext) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/dashboard", { replace: true });
      }
      return;
    }

    navigate(advancedSearchContext.returnTo, {
      state: {
        searchData: advancedSearchContext.searchData,
        restoreFromDetail: true,
        selectedProfileId:
          profile?.martialId || advancedSearchContext.selectedProfileId,
      },
    });
  };

  const handleSkipToNextProfile = () => {
    if (!advancedSearchContext?.profileIds.length) {
      toast.info("No next profile available in this filtered list.");
      return;
    }

    const currentId = profile?.martialId || profileId || "";
    const currentIndex = advancedSearchContext.profileIds.findIndex(
      (item) => String(item) === String(currentId),
    );

    if (currentIndex < 0) {
      toast.info("Current profile is not part of this filtered list.");
      return;
    }

    const nextProfileId = advancedSearchContext.profileIds[currentIndex + 1];

    if (!nextProfileId) {
      toast.info("This is the last profile in the selected filtered list.");
      return;
    }

    navigate(`/admin/profile/detail/${nextProfileId}`, {
      state: {
        source: "advanced-search-results",
        returnTo: advancedSearchContext.returnTo,
        searchData: advancedSearchContext.searchData,
        profileIds: advancedSearchContext.profileIds,
        currentIndex: currentIndex + 1,
        selectedProfileId: nextProfileId,
      },
    });
  };
  const handleShortList = async () => {
    try {
      const targetId = profile._id;
      if (!targetId) {
        console.warn("Unable to determine profile id for match", targetId);
        return;
      }
      // 👇 Call backend API
      const response = await postRequest("/bureau/profiles/shortlist/toggle", {
        profileId: targetId,
      });
      if (response?.success) {
        toast.success(response.message || "Shortlist updated successfully");
        fetchProfile();
      } else {
        toast.warn(response?.message || "Could not update shortlist");
      }
    } catch (error) {
      console.error("Error toggling shortlist:", error);
      toast.error("Something went wrong while updating shortlist");
    }
  };

  const handleTakeProfile = () => {
    const bureauMobileNumber = String(bureauInfo?.mobileNumber || "").trim();
    const targetNumber = bureauMobileNumber.replace(/\D/g, "");

    if (!targetNumber) {
      toast.error("Your bureau mobile number is not available.");
      return;
    }

    const message = [
      "I need this profile",
      `Profile ID: ${profile.martialId || "N/A"}`,
      `Name: ${profile.fullName || "N/A"}`,
      `Bureau Name: ${bureauInfo?.bureauName || "Self Registered"}`,
      `Mobile Number: ${bureauMobileNumber || "N/A"}`,
    ].join("\n");

    const whatsappUrl = `https://wa.me/${targetNumber}?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const handleSwitchProfile = () => {
    const martialId = profile.martialId;
    const password = profile.password;

    const url = `https://meemarriage.com/login?martialId=${encodeURIComponent(
      martialId,
    )}&password=${encodeURIComponent(password)}`;

    window.open(url, "_blank"); // opens in new tab
  };

  const formatINR = (value?: string | number) => {
    if (!value) return "";
    const num = Number(value);
    if (isNaN(num)) return value;
    return `₹ ${num.toLocaleString("en-IN")}`;
  };
  const formatPropertyValue = (value?: number | string) => {
    if (!value) return "";

    const num = Number(value);
    if (isNaN(num)) return value;

    // 1 Crore and above
    if (num >= 10000000) {
      const crore = num / 10000000;

      if (crore === 1) return "1 Crore above";
      if (crore > 1) return `${crore} Crores above`;

      return "1 Crore above";
    }

    // Lakhs
    const lakh = num / 100000;
    return `${lakh} Lakh${lakh > 1 ? "s" : ""}`;
  };

  const getPaymentStatusLabel = (status: any) => {
    if (status == null || status === "") return "New";
    const value = String(status).toLowerCase();
    if (value === "free") return "Free";
    if (value === "paid") return "Paid";
    if (value === "redzone") return "Red Zone";
    return String(status);
  };

  const getPaymentStatusClass = (status: any) => {
    if (status == null || status === "") {
      return "bg-gray-100 text-gray-700";
    }
    const value = String(status).toLowerCase();
    if (value === "free") return "bg-blue-100 text-blue-700";
    if (value === "paid") return "bg-green-100 text-green-700";
    if (value === "redzone") return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-700";
  };
  const isProfileSuspended = isAccountSuspended(profile?.status);
  const isOwnProfile = !profile.isOtherBureau;

  return (
    <div className="mb-10">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex min-h-[64px] max-w-7xl items-center gap-3 px-3 sm:px-4 lg:px-6">
          <button
            type="button"
            onClick={handleBackToResults}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-700 transition hover:bg-gray-100 active:bg-gray-200"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-bold text-gray-900 sm:text-lg">
              {profile.fullName || "Profile Details"}
            </h1>

            {profile.martialId && (
              <p className="truncate text-xs font-medium text-gray-500 sm:text-sm">
                {t("navbar.idLabel")} {profile.martialId}
              </p>
            )}
          </div>

          {profile.completeness >= 100 && (
            <VerifiedIcon className="h-5 w-5 shrink-0 text-green-600" />
          )}
        </div>
      </header>

      <div className="min-h-screen overflow-x-hidden bg-gray-50 pt-4 pb-28 md:pb-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-3 sm:px-4 lg:flex-row lg:px-6">
          {/* ===== Left / Main Section ===== */}

          <div className="min-w-0 flex-1 space-y-4">
            {advancedSearchContext && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-sm">
                {/* <button
                  type="button"
                  onClick={handleBackToResults}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Back to Results
                </button> */}
                <button
                  type="button"
                  onClick={handleSkipToNextProfile}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  {t("skip")}
                </button>
              </div>
            )}
            {/* PROFILE CARD */}
            <div className="w-full rounded-lg bg-white shadow-2xl overflow-hidden">
              <div className="flex flex-col sm:flex-row">
                {/* Profile Image - Left side on desktop */}
                <div className="md:w-80 sm:flex-shrink-0">
                  <ProfileImage
                    profile={profile}
                    UPLOAD_URL={UPLOAD_URL}
                    getGenderPlaceholder={getGenderPlaceholder}
                  />
                  {isProfileSuspended && (
                    <div className="px-4 py-2 text-center text-sm font-semibold text-red-700 bg-red-50 border-t border-red-100">
                      Profile is suspended
                    </div>
                  )}
                </div>
                {/* Profile Content - Right side on desktop */}
                <div className="min-w-0">
                  <div className="mt-4 flex flex-1 flex-col gap-4 px-5 sm:flex-row sm:gap-6">
                    {/* Profile Header Info */}
                    <div className="flex-1 space-y-3 sm:space-y-4">
                      <div className="flex flex-wrap items-center gap-2 pt-2 sm:gap-4">
                        <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                          {t("matches.labels.score")} {profile.completeness}%
                        </span>
                        {profile.isOtherBureau && (
                          <div className="flex items-center gap-1.5 bg-green-100 px-2 py-1 rounded-full">
                            <div className="w-2 h-2 bg-green-600 rounded-full"></div>

                            <span className="text-xs font-semibold text-green-700">
                              {profile.visibleToAll === "true"
                                ? t("matches.labels.public")
                                : "private"}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1 text-green-600">
                            <Eye size={16} />
                            <span className="text-sm font-semibold">
                              {profile.views} {t("matches.labels.views")}
                            </span>
                          </div>
                          {isOwnProfile && (
                            <button
                              type="button"
                              onClick={() => setIsSettingsOpen(true)}
                              aria-haspopup="dialog"
                              aria-expanded={isSettingsOpen}
                              aria-controls="profile-settings-panel"
                              className="inline-flex items-center gap-1.5 rounded-full bg-blue-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-950"
                            >
                              <Settings size={16} aria-hidden="true" />
                              Settings
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 truncate">
                            {profile.fullName}
                          </h1>
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="text-xs flex items-center gap-2 sm:text-sm font-semibold text-gray-800 bg-gray-100 rounded-full px-2 py-1">
                              <span>
                                {t("navbar.idLabel")} {profile.martialId}{" "}
                              </span>
                              {profile.completeness >= 100 && (
                                <VerifiedIcon size={16} color="green" />
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  copyTextToClipboard(
                                    profile.martialId,
                                    "Profile ID copied.",
                                    "Unable to copy profile ID.",
                                  )
                                }
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-slate-600 transition hover:bg-slate-200"
                                aria-label="Copy profile ID"
                              >
                                <Copy size={13} />
                              </button>
                            </div>
                            <span className="text-xs flex gap-1 sm:text-sm font-semibold text-blue-600 bg-blue-100 rounded-full px-2 py-1">
                              {profile.citizenship}
                            </span>
                            <div
                              className={`text-xs sm:text-sm font-semibold rounded-full px-2 py-1 ${getPaymentStatusClass(
                                profile.paymentStatus,
                              )}`}
                            >
                              {getPaymentStatusLabel(profile.paymentStatus)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                        <p className="text-xs flex text-gray-800 bg-gray-100 rounded-full px-2 py-1">
                          {t("matches.labels.lastSeen")}{" "}
                          <span className="font-semibold ml-1">
                            {formatDate(profile.lastSeenAt)}
                          </span>
                        </p>
                        <p className="text-xs flex text-gray-800 bg-gray-100 rounded-full px-2 py-1">
                          {t("createdAt")}{" "}
                          <span className="font-semibold">
                            {formatDate(profile.createdAt)}
                          </span>
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-1 sm:gap-2 text-xs sm:text-sm">
                        {profile.maritalStatus && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {profile.maritalStatus}
                          </span>
                        )}

                        <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                          {t("matches.labels.createdBy")}{" "}
                          {formatCreatedBy(profile?.createdBy)}
                        </span>
                        {profile.time && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {t("birth time")}: {profile.time}
                          </span>
                        )}
                        {profile.age && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {profile.age} {t("yrs")}
                          </span>
                        )}

                        {profile.height && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {profile.height}
                          </span>
                        )}
                        {profile.caste && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {profile.caste}
                          </span>
                        )}
                        {profile.education && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {profile.education}
                          </span>
                        )}
                        {profile.employmentStatus && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {formatEmploymentStatus(profile.employmentStatus)}
                          </span>
                        )}
                        {profile.occupation && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {profile.occupation}
                          </span>
                        )}
                        {profile.jobLocation && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {t("job_location")}: {profile.jobLocation}
                          </span>
                        )}
                        {profile.annualIncome && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full">
                            {profile.annualIncome}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-1 sm:gap-2 text-xs sm:text-sm">
                        {profile.district && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full font-semibold">
                            {profile.district}
                          </span>
                        )}
                        {profile.state && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full font-semibold">
                            {profile.state}
                          </span>
                        )}
                        {profile.country && (
                          <span className="px-2 py-1 sm:px-3 bg-gray-100 rounded-full font-semibold">
                            {profile.country}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {profile.isOtherBureau && (
                    <div className="mt-4 mb-4 px-5">
                      <div className="grid gap-2 sm:gap-3">
                        {/* Contact + Shortlist - Single Row */}
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          {isCreatedBySuperAdmin(profile.createdBy) && (
                            <button
                              className="w-full px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold"
                              onClick={handleTakeProfile}
                            >
                              <MessageCircle size={14} />
                              <span>{t("Take This Profile")}</span>
                            </button>
                          )}
                          {!hideContactDetailsForProfile && (
                            <PaidContactDetails
                              key={profile.martialId}
                              profileId={profile.martialId}
                              hasPaidMembership={hasPaidMembership}
                              owner={{
                                mobileNumber: profile.ownerMobile,
                                bureauName: profile.bureauName,
                                bureauAddress: profile.bureauAddress,
                              }}
                              onUnlocked={(contact) => setProfile((current) => current ? { ...current, ...contact } : current)}
                            />
                          )}
                          <button
                            className="w-full px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 flex items-center justify-center gap-1 sm:gap-2 font-semibold text-xs sm:text-sm"
                            onClick={handleShortList}
                          >
                            {profile.isShortlisted ? (
                              <div className="flex gap-2 text-orange-500">
                                <Star
                                  className="w-4 h-4 text-orange-500"
                                  fill="currentColor"
                                />
                                {t("dashboard.count.shortlisted")}
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <Star className="w-4 h-4" />
                                {t("shortlist")}
                              </div>
                            )}
                          </button>
                        </div>

                        {/* Other Buttons - 3 in Single Row */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-3">
                          <button
                            className="w-full px-4 py-2 border border-gray-300 rounded 
                       flex items-center justify-center gap-1 sm:gap-2 
                       text-xs sm:text-sm text-center 
                       whitespace-normal break-words
                       hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                            onClick={() => handleShareProfile()}
                            disabled={reportLoading}
                          >
                            <Share2 size={16} />
                            <span>{t("matches.buttons.share")}</span>
                          </button>

                          <button
                            className="w-full px-4 py-2 border border-gray-300 bg-red-400 text-white rounded 
                       flex items-center justify-center gap-1 sm:gap-2 
                       text-xs sm:text-sm text-center 
                       whitespace-normal break-words
                       hover:bg-red-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            onClick={openReportModal}
                            disabled={reportLoading}
                          >
                            <BadgeAlert size={14} />
                            <span>{t("matches.buttons.report")}</span>
                          </button>

                          <button
                            className="w-full px-4 py-2 border border-gray-300 rounded 
                       flex items-center justify-center gap-1 sm:gap-2 
                       text-xs sm:text-sm text-center 
                       whitespace-normal break-words
                       hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                            onClick={() => setIsDownloadModalOpen(true)}
                            disabled={reportLoading}
                          >
                            <Download size={14} />
                            <span>{t("Download Biodata")}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-1 flex-col gap-4 rounded-lg bg-white p-4 shadow-2xl sm:ml-5 sm:flex-row sm:gap-6">
              <div className="flex flex-col gap-3">
                {profile.voiceMessage && !removeExistingVoice && (
                  <VoicePlayer
                    src={resolveVoiceMessageUrl(profile.voiceMessage)}
                  />
                )}
                {!profile.isOtherBureau && (
                  <div className="px-5 justify-center">
                    {profile.voiceMessage ? (
                      <div className="flex flex-row gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setVoiceFile(null);
                            setRemoveExistingVoice(false);
                            setIsVoiceEditorOpen((prev) => !prev);
                          }}
                          className="rounded-lg bg-pink-600 px-2 py-2 text-sm font-semibold text-white transition hover:bg-pink-700 flex items-center gap-1 gap-2 sm:gap-2"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveVoiceMessage}
                          disabled={isVoiceSaving}
                          className="rounded-lg border border-red-200 bg-red-50 px-2 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70 flex items-center gap-1 sm:gap-2"
                        >
                          <Trash2 size={14} />
                          {isVoiceSaving ? "Removing..." : ""}
                        </button>
                        <button
                          type="button"
                          onClick={handleShareVoiceMessageOnWhatsApp}
                          disabled={isVoiceSaving || isVoiceSharing}
                          className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-70 flex items-center gap-1 sm:gap-2"
                        >
                          <Share2 size={14} />
                          {isVoiceSharing ? "Sharing..." : "Share"}
                        </button>
                      </div>
                    ) : (
                      <div className="rounded-2xl">
                        <VoiceMessageField
                          initialUrl={
                            removeExistingVoice
                              ? ""
                              : resolveVoiceMessageUrl(
                                  profile.voiceMessage || "",
                                )
                          }
                          onRecordingChange={setIsVoiceRecording}
                          onRemoveExistingChange={setRemoveExistingVoice}
                          onFileChange={(file) => {
                            setVoiceFile(file);
                            if (file) {
                              setRemoveExistingVoice(false);
                            }
                          }}
                          handleSaveVoiceMessage={handleSaveVoiceMessage}
                          isVoiceSaving={isVoiceSaving}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* PERSONAL INFO */}
            <ProfileSection
              title={t("personal_information")}
              icon={User}
              data={[
                [t("date_of_birth"), formatDate(profile.dateOfBirth)],
                [t("birth time"), profile.time],
                [t("age"), profile.age],
                [t("birth_place"), profile.birthPlace],
                [t("birth_time"), profile.birthTime],
                [t("height"), profile.height],
                [t("weight"), profile.weight],
                [t("body_type"), profile.bodyType],
                [t("languages_known"), profile.spokenLanguages],
                [t("matches.labels.createdBy"), profile.createdBy],
                [t("marital_status"), profile.maritalStatus],
                [t("lives_in"), profile.location],
              ]}
            />

            {/* HABITS */}
            <section className="bg-white rounded-lg shadow-2xl p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <Star className="text-pink-600" size={18} /> {t("habits")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 py-1.5 border-b border-gray-100">
                  <span className="min-w-0 text-black [overflow-wrap:anywhere]">{t("eating_habits")}</span>
                  <span aria-hidden="true" className="text-gray-600">:</span>
                  <span className="min-w-0 font-medium text-left [overflow-wrap:anywhere]">
                    {profile.eatingHabits}
                  </span>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 py-1.5 border-b border-gray-100">
                  <span className="min-w-0 text-black [overflow-wrap:anywhere]">{t("smoking_habits")}</span>
                  <span aria-hidden="true" className="text-gray-600">:</span>
                  <span className="min-w-0 font-medium text-left [overflow-wrap:anywhere]">
                    {profile.smokingHabits}
                  </span>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 py-1.5 border-b border-gray-100">
                  <span className="min-w-0 text-black [overflow-wrap:anywhere]">{t("drinking_habits")}</span>
                  <span aria-hidden="true" className="text-gray-600">:</span>
                  <span className="min-w-0 font-medium text-left [overflow-wrap:anywhere]">
                    {profile.drinkingHabits}
                  </span>
                </div>
              </div>
            </section>

            {/* RELIGION & CASTE DETAILS */}

            <ProfileSection
              title={t("religion_caste_details")}
              icon={User}
              data={[
                [t("religion"), profile.religion],
                [t("mother_tongue"), profile.motherTongue],
                [t("caste"), profile.caste],
                [t("sub_caste"), profile.subcaste],
                [t("gothram"), profile.gotram],
                [t("dosham"), profile.dosham],
                [t("UncleGothram"), profile.gotram],
              ]}
            />

            <ProfileSection
              title={t("horoscope_information")}
              icon={User}
              data={[
                [t("star"), profile.star],
                [t("rashi"), profile.raasi],
                [t("dosham"), profile.dosham],
              ]}
            />

            <ProfileSection
              title={t("professional_details")}
              icon={User}
              data={[
                [t("education"), profile.education],
                [
                  t("employment"),
                  formatEmploymentStatus(profile.employmentStatus),
                ],
                [t("occupation"), profile.occupation],
                [t("income"), profile.annualIncome],
                [t("job_location"), profile.jobLocation],
              ]}
            />

            {/* FAMILY */}
            <ProfileSection
              title={t("family_details")}
              icon={User}
              data={[
                [t("father_employee"), profile.fatherEmployee],
                [t("father_occupation"), profile.fatherOccupied],
                [t("mother_employee"), profile.motherEmployee],
                [t("mother_occupation"), profile.motherOccupied],
                [t("family_type"), profile.familyType],
                [t("family_value"), profile.familyValue],
                [t("total_brothers"), profile.totalBrothers],
                [t("elder_brothers"), profile.elderBrothers],
                [t("younger_brothers"), profile.youngerBrothers],
                [t("total_sisters"), profile.totalSisters],
                [t("elder_sisters"), profile.elderSisters],
                [t("younger_sisters"), profile.youngerSisters],
                [t("native_place"), profile.originalLocation],
              ]}
            />

            {/* PROPERTY DETAILS */}

            <ProfileSection
              title={t("property_details")}
              icon={User}
              data={[
                [t("house_type"), profile.houseType],
                [t("number_of_houses"), profile.numberOfHouses],
                [
                  t("house_sq_feet"),
                  profile.houseSqFeet ? `${profile.houseSqFeet} yards` : "",
                ],

                [t("house_value"), formatPropertyValue(profile.houseValue)],

                [t("house_location"), profile.houseLocation],
                [t("commercial_property"), profile.commercialshops],

                [
                  t("commercialSqYards"),
                  profile.shopssqyards ? `${profile.shopssqyards} yards` : "",
                ],

                [t("monthly_rent"), formatINR(profile.monthlyRent)],

                [t("open_plots"), profile.openPlots],

                [
                  t("open_plots_sq_yards"),
                  profile.openPlotsSqFeet
                    ? `${profile.openPlotsSqFeet} yards`
                    : "",
                ],

                [
                  t("open_plots_value"),
                  formatPropertyValue(profile.openPlotsValue),
                ],

                [t("plotLocation"), profile.openPlotsLocation],

                [
                  t("agriculture_land"),
                  profile.agricultureLand
                    ? `${profile.agricultureLand} acres`
                    : "",
                ],

                [t("flatType"), profile.flatType],

                [t("flatValue"), formatPropertyValue(profile.flatValue)],

                [
                  t("agriculture_land_value"),
                  formatPropertyValue(profile.agricultureLandValue),
                ],

                [
                  t("profile.agricultureLocation"),
                  profile.agricultureLandLocation,
                ],

                [
                  t("total_property_value"),
                  formatPropertyValue(profile.totalPropertiesValue),
                ],
              ]}
            />

            {/* {profile.isOtherBureau && (
              <section className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <Phone className="text-pink-600" size={18} />
                  {t("contact_information")}
                </h2>

                {!hasPaidMembership ? (
                  <div className="text-gray-600 text-sm sm:text-base">
                    <p
                      className="mb-2 text-orange-500 font-medium text-xs sm:text-sm hover:underline cursor-pointer"
                      onClick={() =>
                        navigate("/admin/membership/plans", {
                          state: { fromShare: true },
                        })
                      }
                    >
                      {t("contact.upgradeMessage")}
                    </p>
                  </div>
                ) : (
                  <>
                    {!showFullNumber ? (
                      <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center py-2 gap-1">
                        <span className="text-gray-600 text-xs sm:text-sm">
                          {t("contact.mobileNumber")}
                        </span>
                        <button
                          className="text-orange-500 text-xs sm:text-sm font-medium"
                          onClick={() => setShowFullNumber(true)}
                        >
                          {t("contact.viewDetails")}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3 text-gray-700 text-sm sm:text-base">
                        <p className="text-xs text-gray-500">
                          {t("contact.protectedBy")}{" "}
                          <b>
                            {isCreatedBySuperAdmin(profile.createdBy)
                              ? "Super Admin"
                              : profile.bureauName}{" "}
                            {t("contact.marriageBureau")}
                          </b>
                          .
                        </p>
                        <div className="flex flex-col gap-1">
                          {isCreatedBySuperAdmin(profile.createdBy) ? null : (
                            <>
                              <span>
                                <b>{t("contact.ownerName")}</b>{" "}
                                {profile.bureauName ?? "N/A"}
                              </span>
                              <span>
                                <b>{t("contact.bureauAddress")}</b>{" "}
                                {profile.bureauAddress ?? "N/A"}
                              </span>
                            </>
                          )}
                          <div className="flex flex-wrap items-center gap-2">
                            <span>
                              <b>{t("contact.contact")}</b>{" "}
                              {profile.countryCode
                                ? `${profile.countryCode} `
                                : ""}
                              {isCreatedBySuperAdmin(profile.createdBy)
                                ? profile.mobileNumber
                                : profile.ownerMobile ?? "N/A"}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                copyTextToClipboard(
                                  `${profile.countryCode ? `${profile.countryCode} ` : ""}${
                                    isCreatedBySuperAdmin(profile.createdBy)
                                      ? profile.mobileNumber
                                      : profile.ownerMobile ?? ""
                                  }`.trim(),
                                  "Mobile number copied.",
                                  "Unable to copy mobile number.",
                                )
                              }
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
                              aria-label="Copy mobile number"
                            >
                              <Copy size={14} />
                            </button>
                          </div>
                        </div>
                        {isCreatedBySuperAdmin(profile.createdBy) ? null : (
                          <div className="flex items-center gap-3 mt-2">
                            <a
                              href={`tel:${profile.ownerMobile}`}
                              className="bg-green-500 text-white px-3 py-1 rounded-md text-xs sm:text-sm hover:bg-green-600 transition"
                            >
                              {t("contact.call")}
                            </a>
                            <a
                              href={`https://wa.me/${profile.countryCode?.replace(
                                "+",
                                ""
                              )}${profile.ownerMobile}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-green-600 text-white px-3 py-1 rounded-md text-xs sm:text-sm hover:bg-green-700 transition"
                            >
                              {t("contact.whatsapp")}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </section>
            )} */}
            <ProfileGallery profile={profile} />

            {/* ABOUT */}
            {(profile.customWords?.length || profile.voiceMessage) && (
              <section className="bg-white rounded-lg shadow-2xl p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <User className="text-pink-600" size={18} />{" "}
                  {t("about_myself")}
                </h2>
                {buildAboutText(profile)}
              </section>
            )}

            {profile.extraTalentedSkills && (
              <section className="bg-white rounded-lg shadow-2xl p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <User className="text-pink-600" size={18} />{" "}
                  {t("extra_talented_skills")}
                </h2>
                {buildTalents(profile)}
              </section>
            )}

            <GroupedProfileSection
              title={t("partner_preference")}
              icon={User}
              data={[
                {
                  title: t("preferences.basic"),
                  items: [
                    [
                      t("preferences.preferredAge"),
                      profile.agePreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredHeight"),
                      profile.heightPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredMaritalStatus"),
                      profile.maritalStatusPreferences?.join(", "),
                    ],
                    [
                      t("preferences.physicalStatus"),
                      profile.physicalStatusPreference,
                    ],
                    [
                      t("preferences.eatingHabits"),
                      profile.eatingHabitsPreference,
                    ],
                    [
                      t("preferences.drinkingHabits"),
                      profile.drinkingHabitsPreference,
                    ],
                    [
                      t("preferences.smokingHabits"),
                      profile.smokingHabitsPreference,
                    ],
                  ],
                },
                {
                  title: t("preferences.religious"),
                  items: [
                    [
                      t("preferences.preferredReligion"),
                      profile.religionPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredMotherTongue"),
                      profile.motherTonguePreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredCaste"),
                      profile.castePreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredSubcaste"),
                      profile.subCastePreferences?.join(", "),
                    ],
                  ],
                },
                {
                  title: t("preferences.property"),
                  items: [
                    [
                      t("preferences.preferredHouseType"),
                      profile.ownHousePreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredHouseSqYards"),
                      profile.squareYardPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredMonthlyRent"),
                      profile.monthlyRentPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredOpenPlot"),
                      profile.plotPreference?.join(", "),
                    ],
                    [
                      t("preferences.preferredFlat"),
                      profile.flatPreference?.join(", "),
                    ],
                    [
                      t("preferences.preferredOwnLocation"),
                      profile.ownLocationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredAgriculturalLand"),
                      profile.agricultureLandPreference?.join(", "),
                    ],
                    [
                      t("preferences.totalPropertyValue"),
                      profile.totalPropertyValuePreference?.join(", "),
                    ],
                  ],
                },
                {
                  title: t("preferences.professional"),
                  items: [
                    [
                      t("preferences.preferredEducation"),
                      profile.partnerEducationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredEmployment"),
                      profile.partnerOccupationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredOccupation"),
                      profile.partnerOccupationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredAnnualIncome"),
                      profile.annualIncomePreference?.join(" ,"),
                    ],
                    [
                      t("preferences.preferredFamily"),
                      profile.familyPreferences?.join(" ,"),
                    ],
                    [
                      t("preferences.preferredSettledLocation"),
                      profile.settledLocationPreferences?.join(" ,"),
                    ],
                  ],
                },
                {
                  title: t("preferences.location"),
                  items: [
                    [
                      t("preferences.preferredCountry"),
                      profile.countryLocationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredState"),
                      profile.stateLocationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredCity"),
                      profile.cityLocationPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredCitizenship"),
                      profile.citizenshipPreferences?.join(", "),
                    ],
                  ],
                },
                {
                  title: t("preferences.horoscope"),
                  items: [
                    [
                      t("preferences.preferredStar"),
                      profile.starPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredRashi"),
                      profile.rashiPreferences?.join(", "),
                    ],
                    [
                      t("preferences.preferredDosham"),
                      profile.doshamPreferences?.join(", "),
                    ],
                  ],
                },
                {
                  title: t("preferences.habits"),
                  items: [
                    [
                      t("eating_habits"),
                      profile.eatingHabitsPreferences?.join(", "),
                    ],
                    [
                      t("drinking_habits"),
                      profile.drinkingHabitsPreferences?.join(", "),
                    ],
                    [
                      t("smoking_habits"),
                      profile.smokingHabitsPreferences?.join(", "),
                    ],
                  ],
                },
              ]}
            />
          </div>
        </div>
        {isReportModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-red-600">
                  {t("report.title")}
                </h2>
                <button
                  type="button"
                  onClick={closeReportModal}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close report modal"
                  disabled={reportLoading}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">
                  {t("report.reportingProfile")}
                </p>
                <p className="font-semibold">
                  {profile.fullName || "Unknown User"}
                </p>
                <p className="text-sm text-gray-600">
                  {t("report.maritalId")} {selectedProfileMartialId ?? "N/A"}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("report.reasonLabel")}
                </label>
                <select
                  value={reportReason}
                  onChange={(event) => setReportReason(event.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="">{t("report.reasonPlaceholder")}</option>
                  {reportReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("report.detailsLabel")}
                </label>
                <textarea
                  value={reportDescription}
                  onChange={(event) => setReportDescription(event.target.value)}
                  placeholder={t("report.detailsPlaceholder")}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={4}
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={closeReportModal}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={reportLoading}
                >
                  {t("bottomNav.modal.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleReportSubmit}
                  disabled={reportLoading || !reportReason.trim()}
                  className="flex-1 py-2 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {reportLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Reporting...
                    </>
                  ) : (
                    "Submit Report"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <BottomNavbar />

      {isSettingsOpen && isOwnProfile && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
          onClick={(event) => {
            if (event.target === event.currentTarget) setIsSettingsOpen(false);
          }}
        >
          <div
            ref={settingsPanelRef}
            id="profile-settings-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-settings-title"
            tabIndex={-1}
            className="flex max-h-[90dvh] w-full max-w-2xl flex-col rounded-t-3xl bg-white shadow-xl outline-none"
          >
            <div className="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-gray-300" />
            <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4">
              <h2 id="profile-settings-title" className="flex items-center gap-2 text-lg font-bold text-gray-900">
                <Settings size={20} aria-hidden="true" /> Profile Settings
              </h2>
              <button
                type="button"
                onClick={() => setIsSettingsOpen(false)}
                aria-label="Close profile settings"
                className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-5 overflow-y-auto overscroll-contain px-5 pt-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              <ProfilePrivacyAndInsights
                key={profile.martialId}
                profile={profile}
                onVisibilityChanged={(isPublic, contactPrivacy) => setProfile((current) => current ? { ...current, visibleToAll: String(isPublic), contactPrivacy } : current)}
              />
              <section aria-label="Profile actions">
                <h3 className="mb-3 text-base font-bold text-gray-900">Actions</h3>
                  <div className="mt-2 grid grid-cols-6 gap-2 sm:gap-3">
                    <div className="relative col-span-2 overflow-visible min-w-0">
                      <button
                        className="flex w-full items-center justify-center gap-2 rounded border border-gray-300 px-4 py-2 text-xs hover:bg-gray-50 sm:text-sm"
                        onClick={() => {
                          if (!hasPaidMembership) {
                            navigate("/admin/membership/plans", {
                              state: { fromShare: true },
                            });

                            return;
                          }
                          handleSwitchProfile();
                        }}
                      >
                        <Bell size={16} />
                        <RefreshCw size={14} />
                        <span>{t("matches.buttons.switch")}</span>
                      </button>

                      {profile?.switchCount ? (
                        <span className="absolute right-2 top-0 z-30 -translate-y-1/2 inline-flex min-w-[16px] h-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white shadow-sm">
                          {profile.switchCount}
                        </span>
                      ) : null}
                    </div>
                    <button
                      className="col-span-2 flex w-full items-center justify-center gap-1 rounded border border-gray-300 px-4 py-2 text-xs hover:bg-gray-50 sm:gap-2 sm:text-sm"
                      onClick={handleManageProfile}
                    >
                      <Settings size={14} /> {t("matches.buttons.manage")}
                    </button>
                    <button
                      className={`col-span-2 flex w-full items-center justify-center gap-1 rounded px-4 py-2 text-xs font-semibold sm:gap-2 sm:text-sm
                      ${
                        profile.isShortlisted
                          ? "bg-orange-500 hover:bg-orange-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50 text-gray-800"
                      }`}
                      onClick={handleShortList}
                    >
                      {profile.isShortlisted ? (
                        <div className="flex gap-2 items-center">
                          <Star className="w-4 h-4" fill="currentColor" />
                          {t("dashboard.count.shortlisted")}
                        </div>
                      ) : (
                        <div className="flex gap-2 items-center">
                          <Star className="w-4 h-4" />
                          {t("shortlist")}
                        </div>
                      )}
                    </button>
                    <button
                      className="col-span-3 flex w-full items-center justify-center gap-1 rounded border border-gray-300 px-4 py-2 text-xs hover:bg-gray-50 sm:gap-2 sm:text-sm"
                      onClick={() => {
                        navigate(
                          `/admin/edit-profile/${profile.martialId}`,
                        );
                      }}
                    >
                      <Settings size={14} /> {t("Edit")}
                    </button>
                    <button
                      className="col-span-3 flex w-full items-center justify-center gap-1 rounded border border-gray-300 px-4 py-2 text-xs hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:gap-2 sm:text-sm"
                      onClick={() => {
                        setIsSettingsOpen(false);
                        handleShareProfile();
                      }}
                      disabled={reportLoading}
                    >
                      <Share2 size={14} /> {t("matches.buttons.share")}
                    </button>
                    <button
                      className="col-span-6 flex w-full items-center justify-center gap-1 rounded border border-pink-300 px-4 py-2 text-xs text-pink-700 hover:bg-pink-50 sm:gap-2 sm:text-sm"
                      onClick={() =>
                        navigate(
                          `/admin/profile/suitable/${encodeURIComponent(
                            profile.martialId,
                          )}`,
                        )
                      }
                    >
                      <Heart size={14} /> View Suitable Profiles
                    </button>
                    {isOwnProfile && (
                      <button
                        className="col-span-6 flex w-full items-center justify-center gap-1 rounded border border-amber-300 px-4 py-2 text-xs text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60 sm:gap-2 sm:text-sm"
                        onClick={() => {
                          setIsSettingsOpen(false);
                          setIsMoveToDirectModalOpen(true);
                        }}
                        disabled={moveToDirectLoading}
                      >
                        <Trash2 size={14} /> Dont need this profile
                      </button>
                    )}
                    {!profile.isOtherBureau &&
                    !isMediatorCreatedProfile && (
                      <div className="col-span-6 mt-2">
                        <div className="grid gap-3">
                          <div>
                            <span className="mb-2 block text-xs font-semibold text-gray-600">
                              Move to
                            </span>
                            <div className="grid grid-cols-3 gap-2">
                              {profile.paymentStatus === "free" ? null : (
                                <button
                                  onClick={() =>
                                    handlePaymentStatusChange("free")
                                  }
                                  disabled={paymentUpdating !== null}
                                  className={`w-full rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                                    paymentUpdating
                                      ? "cursor-not-allowed bg-gray-200 text-gray-500"
                                      : "bg-blue-600 text-white hover:bg-blue-700"
                                  }`}
                                >
                                  Visit
                                </button>
                              )}
                              {profile.paymentStatus === "paid" ? null : (
                                <button
                                  onClick={() => {
                                    setIsSettingsOpen(false);
                                    handleMoveToPaid();
                                  }}
                                  disabled={paymentUpdating !== null}
                                  className={`w-full rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                                    paymentUpdating
                                      ? "cursor-not-allowed bg-gray-200 text-gray-500"
                                      : "bg-green-600 text-white hover:bg-green-700"
                                  }`}
                                >
                                  Paid
                                </button>
                              )}
                              {profile.paymentStatus === "redzone" ? null : (
                                <button
                                  onClick={() =>
                                    handlePaymentStatusChange("redzone")
                                  }
                                  disabled={paymentUpdating !== null}
                                  className={`w-full rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                                    paymentUpdating
                                      ? "cursor-not-allowed bg-gray-200 text-gray-500"
                                      : "bg-red-600 text-white hover:bg-red-700"
                                  }`}
                                >
                                  Red Zone
                                </button>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() =>
                                  handleFollowUpStatusChange("callback")
                                }
                                disabled={
                                  paymentUpdating !== null ||
                                  profile.followUpStatus === "callback"
                                }
                                className={`w-full rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                                  paymentUpdating !== null ||
                                  profile.followUpStatus === "callback"
                                    ? "cursor-not-allowed bg-amber-100 text-amber-700"
                                    : "bg-amber-500 text-white hover:bg-amber-600"
                                }`}
                              >
                                {t("Move to Call Back")}
                              </button>
                              <button
                                onClick={() =>
                                  handleFollowUpStatusChange("not_responding")
                                }
                                disabled={
                                  paymentUpdating !== null ||
                                  profile.followUpStatus === "not_responding"
                                }
                                className={`w-full rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                                  paymentUpdating !== null ||
                                  profile.followUpStatus === "not_responding"
                                    ? "cursor-not-allowed bg-rose-100 text-rose-700"
                                    : "bg-rose-600 text-white hover:bg-rose-700"
                                }`}
                              >
                                {t("Move to Not Responding")}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Existing DownloadModal - unchanged */}
      {isDownloadModalOpen && (
        <DownloadModal
          profile={profile}
          setIsDownloadModalOpen={setIsDownloadModalOpen}
        />
      )}

      {isShareModalOpen && (
        <ShareModal
          profile={profile}
          setIsShareModalOpen={setIsShareModalOpen}
        />
      )}

      {isShareBiodataModalOpen && shareBiodataType && (
        <ShareBiodataModal
          profile={profile}
          setIsShareBiodataModalOpen={setIsShareBiodataModalOpen}
          shareType={shareBiodataType}
        />
      )}
      {isMoveToDirectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Dont need this profile
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  r u shure This profile will be permanently removed from your
                  account; is that okay with you?
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsMoveToDirectModalOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsMoveToDirectModalOpen(false)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                disabled={moveToDirectLoading}
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleMoveToDirectProfiles}
                className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-amber-300"
                disabled={moveToDirectLoading}
              >
                {moveToDirectLoading ? "Processing..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}
      {isWarningModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-3">Notice</h2>

            <p className="text-sm">{t("contact.details.warning")}</p>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => navigate("/admin/user/profile/own")}
                className="px-4 py-2 bg-purple-600 text-white rounded"
              >
                {t("bottomNav.addProfile")}
              </button>

              <button
                onClick={() => setIsWarningModalOpen(false)}
                className="px-4 py-2 border rounded"
              >
                {t("skip")}
              </button>
            </div>
          </div>
        </div>
      )}
      <ServicePreferenceModal
        isOpen={isServicePreferenceModalOpen}
        onClose={closeServicePreferenceModal}
        profile={profile}
        onSuccess={() => {
          closeServicePreferenceModal();
          fetchProfile();
        }}
      />
    </div>
  );
};

export default ProfileDetails;
